import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, MailCheck, AlertCircle } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setError("");
    setLoading(true);
    try {
      await api.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err.message || "Impossible d'envoyer le lien.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="page-forgot-password"
      className="min-h-screen flex flex-col relative overflow-hidden bg-[#F7F8FA]"
    >
      <div
        aria-hidden
        className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#C8FF2F]/25 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#D9FFE8]/40 blur-3xl"
      />

      <header className="relative z-10 px-6 py-8">
        <div className="max-w-md mx-auto">
          <Logo variant="full" size="md" rounded="rounded-lg" />
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md wyvio-fade-in">
          {sent ? (
            <div data-testid="forgot-confirmation">
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#111111] text-[#C8FF2F]">
                <MailCheck size={20} strokeWidth={2} />
              </div>
              <h1 className="mt-6 text-[36px] sm:text-[42px] leading-[1.05] font-semibold tracking-tight text-[#111111]">
                Vérifiez votre boîte mail
              </h1>
              <p className="mt-3 text-[15px] text-[#6B7280] leading-relaxed">
                Si un compte existe pour <span className="text-[#111111] font-medium">{email}</span>,
                nous venons d&apos;envoyer un lien sécurisé pour définir un nouveau mot de passe.
                Le lien est valide 60 minutes.
              </p>
              <p className="mt-2 text-[13px] text-[#9CA3AF] leading-relaxed">
                Pensez à vérifier vos spams. Vous pouvez fermer cette page.
              </p>

              <Link
                to="/login"
                data-testid="forgot-back-to-login"
                className="mt-8 inline-flex items-center gap-2 text-[13px] font-medium text-[#111111] hover:opacity-70 transition-opacity"
              >
                <ArrowLeft size={14} strokeWidth={2} />
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <>
              <h1
                data-testid="forgot-title"
                className="text-[36px] sm:text-[42px] leading-[1.05] font-semibold tracking-tight text-[#111111]"
              >
                Mot de passe oublié
              </h1>
              <p className="mt-3 text-[15px] text-[#6B7280] leading-relaxed">
                Saisissez votre adresse e-mail. Nous vous enverrons un lien
                pour définir un nouveau mot de passe.
              </p>

              <form
                onSubmit={handleSubmit}
                className="mt-10 space-y-4"
                data-testid="forgot-form"
              >
                <div>
                  <label className="text-[12px] font-medium text-[#6B7280]">
                    Adresse e-mail
                  </label>
                  <input
                    data-testid="forgot-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="stephane@prestige-auto.fr"
                    required
                    className="mt-1.5 w-full h-12 rounded-2xl bg-white border border-[#EEF2F5] px-4 text-[14px] text-[#111111] placeholder:text-[#9CA3AF] outline-none focus:border-[#111111] transition-colors"
                  />
                </div>

                <button
                  data-testid="forgot-submit"
                  type="submit"
                  disabled={loading}
                  className="mt-6 w-full h-12 rounded-full bg-[#111111] text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 hover:bg-black active:scale-[0.99] transition-all shadow-[0_10px_30px_-10px_rgba(17,17,17,0.4)] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? "Envoi…" : "Envoyer le lien"}
                  {!loading && <ArrowRight size={15} strokeWidth={2.25} />}
                </button>

                {error && (
                  <div
                    data-testid="forgot-error"
                    className="mt-3 flex items-start gap-2 rounded-xl bg-[#FEE2E2] px-3.5 py-2.5 text-[12.5px] text-[#991B1B]"
                  >
                    <AlertCircle size={13} strokeWidth={2} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
              </form>

              <Link
                to="/login"
                data-testid="forgot-back-to-login"
                className="mt-8 inline-flex items-center gap-2 text-[13px] font-medium text-[#111111] hover:opacity-70 transition-opacity"
              >
                <ArrowLeft size={14} strokeWidth={2} />
                Retour à la connexion
              </Link>
            </>
          )}
        </div>
      </main>

      <footer className="relative z-10 px-6 pb-6">
        <p className="max-w-md mx-auto text-[11px] text-[#9CA3AF] text-center">
          © 2026 wyvio · Un espace collaboratif pour vos ventes automobiles.
        </p>
      </footer>
    </div>
  );
};

export default ForgotPassword;
